/**
 * Landing-only vanilla TS island. NO React, NO Spectrum.
 *
 * Bundle target: < 5 KB gzip. Wires up:
 *   - Copy buttons in code blocks
 *   - Tab switching in the code showcase
 *   - The pricing simulator slider
 *
 * The rest of the landing is fully pre-rendered static HTML — no
 * hydration needed.
 */

import "./styles/landing.css";

function ready(fn: () => void): void {
  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", fn, { once: true });
  } else {
    fn();
  }
}

function wireCopyButtons(): void {
  document.querySelectorAll<HTMLButtonElement>("[data-copy]").forEach((btn) => {
    btn.addEventListener("click", () => {
      const source = btn.dataset.copySource ?? "";
      if (!navigator.clipboard) {
        return;
      }
      navigator.clipboard
        .writeText(source)
        .then(() => {
          btn.setAttribute("aria-pressed", "true");
          const original = btn.textContent ?? "Copiar";
          btn.textContent = "Copiado";
          setTimeout(() => {
            btn.setAttribute("aria-pressed", "false");
            btn.textContent = original;
          }, 1500);
        })
        .catch(() => {
          /* clipboard may be unavailable; fail silently */
        });
    });
  });
}

function wireTabs(): void {
  document.querySelectorAll<HTMLElement>("[data-tabs]").forEach((container) => {
    const tabs = Array.from(
      container.querySelectorAll<HTMLButtonElement>("[data-tab-target]"),
    );
    const panels = tabs.map((t) => {
      const id = t.dataset.tabTarget ?? "";
      return container.querySelector<HTMLElement>(`#panel-${id}`);
    });

    function activate(idx: number): void {
      tabs.forEach((t, i) => {
        const selected = i === idx;
        t.setAttribute("aria-selected", selected ? "true" : "false");
        t.tabIndex = selected ? 0 : -1;
      });
      panels.forEach((p, i) => {
        if (!p) return;
        p.hidden = i !== idx;
      });
    }

    tabs.forEach((t, i) => {
      t.addEventListener("click", () => activate(i));
      t.addEventListener("keydown", (e) => {
        const key = (e as KeyboardEvent).key;
        if (key === "ArrowRight" || key === "ArrowLeft") {
          e.preventDefault();
          const dir = key === "ArrowRight" ? 1 : -1;
          const next = (i + dir + tabs.length) % tabs.length;
          activate(next);
          tabs[next]?.focus();
        }
      });
    });
  });
}

function wireSimulator(): void {
  const root = document.querySelector<HTMLElement>("[data-simulator]");
  if (!root) return;

  const input = root.querySelector<HTMLInputElement>("[data-simulator-input]");
  const docsEl = root.querySelector<HTMLElement>("[data-simulator-docs]");
  const costEl = root.querySelector<HTMLElement>("[data-simulator-cost]");
  const planEl = root.querySelector<HTMLElement>("[data-simulator-plan]");
  if (!input || !docsEl || !costEl || !planEl) return;

  const formatter = new Intl.NumberFormat("es-PE", {
    style: "currency",
    currency: "PEN",
    minimumFractionDigits: 2,
  });

  function compute(docs: number): { cost: number; plan: string } {
    if (docs <= 100) return { cost: 0, plan: "Free" };
    if (docs < 5000) {
      const billable = docs - 100;
      return { cost: billable * 0.1, plan: "Pay-as-you-go" };
    }
    return { cost: docs * 0.05, plan: "Volumen" };
  }

  function update(): void {
    const docs = Number(input!.value);
    const { cost, plan } = compute(docs);
    docsEl!.textContent = docs.toLocaleString("es-PE");
    costEl!.textContent = formatter.format(cost);
    planEl!.textContent = plan;
  }

  input.addEventListener("input", update);
  update();
}

ready(() => {
  wireCopyButtons();
  wireTabs();
  wireSimulator();
});
